import { c as _c } from "react/compiler-runtime";
import { createFieldErrorHandler, getCountryCodeFromLocale } from '@onefootprint/core';
import type { FieldError } from '@onefootprint/hooks';
import { IcoCreditcard24 } from '@onefootprint/icons';
import type { Language, Locale, OnboardingRequirementCollectCardData, VaultData } from '@onefootprint/request-types';
import { Form, PageShell } from '@onefootprint/ui';
import creditcardutils from 'creditcardutils';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CardIcon from './components/card-icon';
import CountryAndZipSection from './components/country-and-zip-section';
import CvcAndExpiryFields from './components/cvc-and-expiry-fields';
import type { CardFormData } from './types';
import createPayload from './utils/create-payload';
import { isCardNumberValid } from './utils/validations';
type CollectCardDataProps = {
  currentRequirement: OnboardingRequirementCollectCardData;
  isLoading: boolean;
  onSubmit: (data: VaultData, onError: (errors: FieldError[]) => void) => void;
  l10n?: {
    locale?: Locale;
    language?: Language;
  };
};

// Maps card data identifier keys to form field names
const cardDiToFormField = (key: string): string => {
  const mapping: Record<string, string> = {
    'card.number': 'cardNumber',
    'card.cvc': 'cvc',
    'card.expiration': 'expiry',
    'card.expiration_month': 'expiry',
    'card.expiration_year': 'expiry',
    'card.name': 'name',
    'card.billing_zip': 'zip',
    'card.billing_country': 'country'
  };
  return mapping[key] ?? key;
};
export const CollectCardData = t0 => {
  const $ = _c(108);
  const {
    l10n,
    currentRequirement,
    isLoading,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "collectCardData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  let t2;
  if ($[1] !== l10n?.locale) {
    t2 = getCountryCodeFromLocale(l10n?.locale) ?? "US";
    $[1] = l10n?.locale;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      defaultValues: {
        country: t2
      }
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const methods = useForm(t3);
  const {
    control,
    setError,
    formState: t4
  } = methods;
  const {
    errors
  } = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = ["country"];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== control) {
    t6 = {
      control,
      name: t5
    };
    $[6] = control;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const [country] = useWatch(t6);
  const [brand, setBrand] = useState("");
  const numDigits = brand === "amex" ? 4 : 3;
  let t7;
  if ($[8] !== currentRequirement) {
    t7 = fieldName => {
      if (fieldName === "cvc") {
        return currentRequirement.collectCvc;
      }
      if (fieldName === "expiry") {
        return currentRequirement.collectExpiration;
      }
      if (fieldName === "zip") {
        return currentRequirement.collectBillingZip;
      }
      if (fieldName === "country") {
        return currentRequirement.collectBillingCountry;
      }
      return false;
    };
    $[8] = currentRequirement;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const collects = t7;
  let t8;
  if ($[10] !== errors || $[11] !== setError) {
    t8 = fieldName_0 => {
      const currentErrors = errors[fieldName_0];
      if (currentErrors?.type === "server") {
        setError(fieldName_0, {
          type: "server",
          message: undefined
        });
      }
    };
    $[10] = errors;
    $[11] = setError;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const clearServerError = t8;
  let T0;
  let t10;
  let t9;
  if ($[13] !== currentRequirement || $[14] !== methods || $[15] !== onSubmit) {
    const handleFieldErrors = createFieldErrorHandler(methods, {
      keyToFieldName: cardDiToFormField
    });
    const handleSubmit = async data => {
      onSubmit(createPayload(data, currentRequirement), handleFieldErrors);
    };
    T0 = FormProvider;
    t10 = methods;
    t9 = methods.handleSubmit(handleSubmit);
    $[13] = currentRequirement;
    $[14] = methods;
    $[15] = onSubmit;
    $[16] = T0;
    $[17] = t10;
    $[18] = t9;
  } else {
    T0 = $[16];
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t(_temp);
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t(_temp2);
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t(_temp3);
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = {
      text: t13,
      type: "submit"
    };
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IcoCreditcard24 color="primary" />;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t) {
    t16 = t(_temp4);
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16) {
    t17 = <div className="flex gap-1 items-center">{t15}<div className="text-primary text-label-2">{t16}</div></div>;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t(_temp5);
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t) {
    t19 = t(_temp6);
    $[34] = t;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t) {
    t20 = t(_temp7);
    $[36] = t;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== clearServerError) {
    t21 = () => clearServerError("name");
    $[38] = clearServerError;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = {
      required: t20,
      onChange: t21
    };
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t18 || $[44] !== t19 || $[45] !== t22) {
    t23 = <Form.Input name="name" label={t18} autoComplete="cc-name" data-dd-privacy="mask" placeholder={t19} rules={t22} />;
    $[43] = t18;
    $[44] = t19;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t) {
    t24 = t(_temp8);
    $[47] = t;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== brand) {
    t25 = <CardIcon brand={brand} />;
    $[49] = brand;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      creditCard: true
    };
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t) {
    t27 = t(_temp9);
    $[52] = t;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== clearServerError) {
    t28 = event => {
      const detectedBrand = creditcardutils.parseCardType(event.target.value);
      setBrand(detectedBrand);
      clearServerError("cardNumber");
    };
    $[54] = clearServerError;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t) {
    t29 = t(_temp10);
    $[56] = t;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t) {
    t30 = {
      validCard: value => isCardNumberValid(value) || t(_temp11)
    };
    $[58] = t;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t28 || $[61] !== t29 || $[62] !== t30) {
    t31 = {
      onChange: t28,
      required: t29,
      validate: t30
    };
    $[60] = t28;
    $[61] = t29;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t24 || $[65] !== t25 || $[66] !== t27 || $[67] !== t31) {
    t32 = <Form.Input name="cardNumber" label={t24} placeholderIcon={t25} autoComplete="cc-number" data-dd-privacy="mask" inputMode="numeric" mask={t26} placeholder={t27} rules={t31} />;
    $[64] = t24;
    $[65] = t25;
    $[66] = t27;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== collects) {
    t33 = collects("cvc");
    $[69] = collects;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== collects) {
    t34 = collects("expiry");
    $[71] = collects;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== clearServerError || $[74] !== numDigits || $[75] !== t33 || $[76] !== t34) {
    t35 = <CvcAndExpiryFields collectsCvc={t33} collectsExpiry={t34} numDigits={numDigits} clearServerError={clearServerError} />;
    $[73] = clearServerError;
    $[74] = numDigits;
    $[75] = t33;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] !== t17 || $[79] !== t23 || $[80] !== t32 || $[81] !== t35) {
    t36 = <section className="flex flex-col gap-4 border border-tertiary rounded p-4">{t17}{t23}{t32}{t35}</section>;
    $[78] = t17;
    $[79] = t23;
    $[80] = t32;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== collects) {
    t37 = collects("zip");
    $[83] = collects;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== collects) {
    t38 = collects("country");
    $[85] = collects;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== clearServerError || $[88] !== country || $[89] !== t37 || $[90] !== t38) {
    t39 = <CountryAndZipSection collectsZip={t37} collectsCountry={t38} country={country} clearServerError={clearServerError} />;
    $[87] = clearServerError;
    $[88] = country;
    $[89] = t37;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t36 || $[93] !== t39) {
    t40 = <div className="flex flex-col gap-4 w-full">{t36}{t39}</div>;
    $[92] = t36;
    $[93] = t39;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== isLoading || $[96] !== t11 || $[97] !== t12 || $[98] !== t14 || $[99] !== t40) {
    t41 = <PageShell title={t11} subtitle={t12} cta={t14} isLoading={isLoading}>{t40}</PageShell>;
    $[95] = isLoading;
    $[96] = t11;
    $[97] = t12;
    $[98] = t14;
    $[99] = t40;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t41 || $[102] !== t9) {
    t42 = <form onSubmit={t9}>{t41}</form>;
    $[101] = t41;
    $[102] = t9;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== T0 || $[105] !== t10 || $[106] !== t42) {
    t43 = <T0 {...t10}>{t42}</T0>;
    $[104] = T0;
    $[105] = t10;
    $[106] = t42;
    $[107] = t43;
  } else {
    t43 = $[107];
  }
  return t43;
};
function _temp($) {
  return $.title;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp3($_1) {
  return $_1.form.cta;
}
function _temp4($_2) {
  return $_2.form.cardDetails;
}
function _temp5($_3) {
  return $_3.form.name.label;
}
function _temp6($_4) {
  return $_4.form.name.placeholder;
}
function _temp7($_5) {
  return $_5.form.name.errors.required;
}
function _temp8($_6) {
  return $_6.form.number.label;
}
function _temp9($_7) {
  return $_7.form.number.placeholder;
}
function _temp10($_8) {
  return $_8.form.number.errors.required;
}
function _temp11($_9) {
  return $_9.form.number.errors.invalid;
}